import React, { useEffect } from "react";
import UpdatesBanner from "../UpdatesBanner/UpdatesBanner";
import "./TPN.css";
import CTA from "../CTA/CTA";

/*
  Styling for every section lives in TPN.css and follows the
  "Global UX & Design Guidelines" (typography, colour, spacing,
  background alternation, motion and recurring UI patterns).
*/

// ─── Shared building blocks ───────────────────────────────────────────────────

/** One page section. `tint` switches the background to the accent-tint colour. */
function Section({ id, tint, label, title, subtitle, children }) {
  return (
    <section id={id} className={`tpn-section${tint ? " tpn-section--tint" : ""}`}>
      <div className="tpn-container tpn-reveal">
        {label && <p className="tpn-eyebrow">{label}</p>}
        <h2 className="tpn-h2">{title}</h2>
        {subtitle && <p className="tpn-lead">{subtitle}</p>}
        {!subtitle && <div style={{ height: "28px" }} />}
        {children}
      </div>
    </section>
  );
}

function Grid({ cols = 3, children }) {
  return <div className={`tpn-grid tpn-cols-${cols}`}>{children}</div>;
}

function Card({ icon, title, children, accent, flat }) {
  return (
    <div className={`tpn-card${accent ? " tpn-card--accent" : ""}${flat ? " tpn-card--flat" : ""}`}>
      {(icon || title) && (
        <div className="tpn-card__head">
          {icon && <span className="tpn-icon" aria-hidden="true">{icon}</span>}
          <h3 className="tpn-h3">{title}</h3>
        </div>
      )}
      {children}
    </div>
  );
}

function Chips({ items }) {
  return (
    <ul className="tpn-chips">
      {items.map((item, i) => <li key={i}>{item}</li>)}
    </ul>
  );
}

function CheckList({ items, mark = "✓", columns }) {
  return (
    <ul className={`tpn-checklist${columns ? " tpn-checklist--cols" : ""}`}>
      {items.map((item, i) => (
        <li key={i}><span className="tpn-tick" aria-hidden="true">{mark}</span><span>{item}</span></li>
      ))}
    </ul>
  );
}

/** Icon + label grid item */
function LabelItem({ icon = "✓", children }) {
  return (
    <div className="tpn-card tpn-label-item">
      <span className="tpn-icon" aria-hidden="true">{icon}</span>
      <span>{children}</span>
    </div>
  );
}

function Callout({ ink, children }) {
  return <div className={`tpn-callout${ink ? " tpn-callout--ink" : ""}`}>{children}</div>;
}

function Note({ children }) {
  return <p className="tpn-body tpn-muted tpn-narrow" style={{ marginTop: "24px" }}>{children}</p>;
}

// ─── 02 — Why You Need This Now ───────────────────────────────────────────────
function WhyNowSection() {
  const dependsOn = [
    "Farmers", "FPOs", "Manufacturers", "Distributors", "Service providers",
    "Brands", "Logistics providers", "Technology partners", "Communities",
  ];
  const realities = [
    "Multiple organisations", "Distributed operations", "Shared business processes", "Interdependent participants",
    "Physical and digital assets", "Fragmented applications", "Distributed data", "Complex workflows",
  ];

  return (
    <Section tint label="Why You Need This Now" title="Agrifood Organisations Are Becoming Ecosystems">
      <div className="tpn-panel tpn-block">
        <p className="tpn-body">Modern agrifood organisations are no longer operated by one organisation alone.</p>
        <p className="tpn-body tpn-muted">They depend on:</p>
        <Chips items={dependsOn} />
        <p className="tpn-body tpn-muted" style={{ marginTop: "20px" }}>…and many other participants.</p>

        <Card flat title="The challenge is not simply connecting them.">
          <p className="tpn-body tpn-strong">The challenge is operating them as one ecosystem.</p>
        </Card>
        
      </div>

      {/* <Grid cols={2}>
        <Card flat title="The challenge is not simply connecting them.">
          <p className="tpn-body tpn-strong">The challenge is operating them as one ecosystem.</p>
        </Card>
        <Card flat title="What makes it hard">
          <CheckList items={realities} mark="•" />
        </Card>
      </Grid> */}

      <Callout>You need an operating environment built for the ecosystem—not another application for the organisation.</Callout>
    </Section>
  );
}

// ─── 03 — The Problem (two-column contrast) ───────────────────────────────────
function ProblemSection() {
  const erp = ["Your employees", "Your processes", "Your resources", "Your internal transactions", "Your enterprise data", "Your business workflows", "Your organisational roles","Your internal governance",];
  const eco = [
    "Multiple organisations", "External participants", "Shared services", "Cross-organisation workflows",
    "Network relationships", "Distributed assets", "Ecosystem data", "Ecosystem governance",
  ];

  return (
    <Section
      label="The Problem"
      title="ERP Manages Your Business. Your Ecosystem Needs More."
      subtitle="ERP systems are designed primarily around the operations of one organisation. An ecosystem network operates across organisational boundaries."
    >
      <div className="tpn-compare">
        <div className="tpn-compare__col">
          <div className="tpn-compare__head">ERP typically manages</div>
          <ul className="tpn-compare__list">
            {erp.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        </div>
        <div className="tpn-compare__col tpn-compare__col--accent">
          <div className="tpn-compare__head">An Ecosystem Network manages</div>
          <ul className="tpn-compare__list">
            {eco.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        </div>
      </div>

      <Callout ink>
        ERP helps you run your organisation.<br />
        <span className="tpn-em">GBR enables you to operate the ecosystem network around it.</span>
      </Callout>
    </Section>
  );
}

// ─── 04 — The Transformation ──────────────────────────────────────────────────
function TransformationSection() {
  const actions = [
    "You define the ecosystem.",
    "You decide who participates.",
    "You configure how participants interact.",
    "You provide digital tools.",
    "You operate workflows.",
    "You govern the ecosystem.",
    "You create value across the ecosystem.",
  ];

  return (
    <Section
      tint
      label="The Transformation"
      title="From a Business You Manage… to an Ecosystem You Operate"
      subtitle={<>Your organisation becomes the <strong>Ecosystem Network Operator (ENO)</strong>.</>}
    >
      <Grid cols={3}>
        {actions.map((a, i) => (
          <div key={i} className="tpn-card">
            <div className="tpn-badge">{String(i + 1).padStart(2, "0")}</div>
            <p className="tpn-body tpn-strong">{a}</p>
          </div>
        ))}
      </Grid>

      <Callout>
        You don't just participate in the ecosystem.<br />
        <span className="tpn-em">You operate it.</span>
      </Callout>
    </Section>
  );
}




// ─── 05 — What Can You Build? ─────────────────────────────────────────────────
function WhatCanYouBuildSection() {
  const around = ["Industry", "Business model", "Supply chain", "Geography", "Community", "Operational objective", "Micro-ecosystems"];
  const flow = ["Participants", "Organisations", "Digital Tools", "Workflows", "Data", "AI Agents", "Devices", "Physical Assets"];
  return (
    <Section label="What Can You Build?" title="Build the Ecosystem Network Your Business Needs">
      <p className="tpn-body" style={{ marginBottom: "24px" }}>Create an ecosystem network around your:</p>
      <div className="tpn-block"><Chips items={around} /></div>
   
       
        
      <p className="tpn-body" style={{ marginBottom: "24px" }}>Start with what you want to achieve and build the ecosystem network around your intent</p>

      <p className="tpn-h3" style={{ marginBottom: "24px" }}>What Is an Ecosystem Network?</p>

      
       <span className="tpn-em">More Than a Network of People</span>
      
      <p className="tpn-body" style={{ marginBottom: "24px" }}>An ecosystem network brings together:</p>

      <ul className="tpn-flow">
        {flow.map((item, i) => (
          <React.Fragment key={i}>
            <li>{item}</li>
            {i < flow.length - 1 && <li className="tpn-arrow" aria-hidden="true">→</li>}
          </React.Fragment>
        ))}
      </ul>

      <p className="tpn-body tpn-narrow">All operating together within a governed ecosystem.</p>
     

      <Callout>
        Your ecosystem can connect people, organisations, digital operations and physical operations through one operating environment.
      </Callout>

    </Section>
  );
}

// ─── 06 — What Is An Ecosystem Network? (chain / flow) ────────────────────────
/* function WhatIsNetworkSection() {
  const flow = ["Participants", "Organisations", "Digital Tools", "Workflows", "Data", "AI Agents", "Devices", "Physical Assets"];

  return (
    <Section tint label="What Is An Ecosystem Network?" title="More Than a Network of People">
      <p className="tpn-body" style={{ marginBottom: "24px" }}>An ecosystem network brings together:</p>

      <ul className="tpn-flow">
        {flow.map((item, i) => (
          <React.Fragment key={i}>
            <li>{item}</li>
            {i < flow.length - 1 && <li className="tpn-arrow" aria-hidden="true">→</li>}
          </React.Fragment>
        ))}
      </ul>

      <p className="tpn-body tpn-narrow">All operating together within a governed ecosystem.</p>
      <p className="tpn-body tpn-muted tpn-narrow" style={{ marginBottom: "48px" }}>
        A network becomes an operating ecosystem when these elements work together.
      </p>

      <Callout>
        Your ecosystem can connect people, organisations, digital operations and physical operations through one operating environment.
      </Callout>
    </Section>
  );
} */

// ─── 07 — How Your Network Works (numbered stepper) ───────────────────────────
function HowNetworkWorksSection() {
  const steps = [
    { title: "Define", desc: "Define your ecosystem objective and select or create a network model." },
    { title: "Configure", desc: "Configure participants, digital tools, workflows, data and governance." },
    { title: "Activate", desc: "Provision the ecosystem network and activate participants." },
    { title: "Connect", desc: "Connect applications, devices, physical assets and data." },
    { title: "Orchestrate", desc: "Coordinate ecosystem activities, interactions and workflows." },
    { title: "Operate", desc: "Run the ecosystem network continuously." },
    { title: "Scale", desc: "Expand participants, digital tools, micro-ecosystems and ecosystem networks." },
  ];

  return (
    <Section tint id="how-your-network-works" label="How Your Network Works" title="One Network. Many Participants. One Operating Model.">
      <ol className="tpn-stepper">
        {steps.map((s, i) => (
          <li key={i} className="tpn-card tpn-step">
            <div className="tpn-step__num">{String(i + 1).padStart(2, "0")}</div>
            <h3 className="tpn-h3">{s.title}</h3>
            <p className="tpn-body tpn-muted">{s.desc}</p>
          </li>
        ))}
      </ol>

      <Callout>Build once. Operate continuously. Scale progressively.</Callout>
    </Section>
  );
}

// ─── 08 — Configure Your Ecosystem (icon + label grid) ────────────────────────
/* function ConfigureSection() {
  const items = [
    "Ecosystem participants", "Participant relationships", "Ecosystem roles", "Digital tools", "Workflows",
    "Applications", "Devices", "Physical assets", "Data", "Governance and operating rules",
  ];

  return (
    <Section
      tint
      label="Configure Your Ecosystem"
      title="Your Ecosystem. Your Operating Model."
      subtitle="Start with your ecosystem intent and configure the network around it."
    >
      <p className="tpn-h3" style={{ marginBottom: "24px" }}>Configure:</p>
      <Grid cols={4}>
        {items.map((item, i) => <LabelItem key={i}>{item}</LabelItem>)}
      </Grid>

      <div className="tpn-block"><Callout>Configure the network around your business—not your business around the software.</Callout></div>
      <p className="tpn-body tpn-muted tpn-narrow">
        The ENO determines how the ecosystem is structured, how participants interact and how operations are executed.
      </p>
    </Section>
  );
} */

// ─── 09 — Ecosystem Network Models ────────────────────────────────────────────
function NetworkModelsSection() {
  const defines = ["Participants", "Organisations", "Roles and relationships", "Digital tools", "Workflows", "Data", "AI Agnet" , "Deviecs", "Physical Assets", "Governance", "Operating structure"];
    const networks = [
    { code: "BEN", name: "Bee Economy Network", desc: "Connects participants across the Bee Economy." },
    { code: "FEN", name: "Farm Enterprise Network", desc: "Connects farms, enterprises and agricultural operations." },
    { code: "CMN", name: "Contract Manufacturing Network", desc: "Connects brands, manufacturers and production ecosystems." },
    { code: "ADN", name: "AgroDealer Network", desc: "Connects agricultural product and service distribution networks." },
    { code: "CBON", name: "Community-Based Organization Network", desc: "Connects community organisations and ecosystem participants." },
    { code: "SCN", name: "Sustainability & Climate Network", desc: "Connects sustainability, climate and impact participants." },
  ];
  return (
    <Section
      label="Ecosystem Network Models"
      title="Start With an Ecosystem Network Model"
      subtitle="Use reusable ecosystem network models as configurable blueprints for building your ecosystem network."
    >
      <p className="tpn-h3" style={{ marginBottom: "24px" }}>A Network Model can define:</p>
      <div className="tpn-block"><Chips items={defines} /></div>

     {/*  <Callout>Configure the model. Instantiate your network. Operate it your way.</Callout> */}
   {/*    <Note>A single Network Model can be configured and instantiated into multiple ecosystem networks.</Note> */}

    <p className="tpn-h3" style={{ marginBottom: "24px" }}>Available Network Models:</p>
    
    <Grid cols={3}>
        {networks.map((n, i) => (
          <div key={i} className="tpn-card">
            <span className="tpn-badge">{n.code}</span>
            <h3 className="tpn-h3">{n.name}</h3>
            <p className="tpn-body tpn-muted">{n.desc}</p>
          </div>
        ))}
      </Grid>
        <Callout>Configure the model. Instantiate your network. Operate it your way.</Callout>
    </Section>
  );
}

// ─── 10 — Available Ecosystem Networks (card grid) ────────────────────────────
/* function AvailableNetworksSection() {
  const networks = [
    { code: "BEN", name: "Bee Economy Network", desc: "Connects participants across the Bee Economy." },
    { code: "FEN", name: "Farm Enterprise Network", desc: "Connects farms, enterprises and agricultural operations." },
    { code: "CMN", name: "Contract Manufacturing Network", desc: "Connects brands, manufacturers and production ecosystems." },
    { code: "ADN", name: "AgroDealer Network", desc: "Connects agricultural product and service distribution networks." },
    { code: "CBON", name: "Community-Based Organization Network", desc: "Connects community organisations and ecosystem participants." },
    { code: "SCN", name: "Sustainability & Climate Network", desc: "Connects sustainability, climate and impact participants." },
  ];

  return (
    <Section
      tint
      label="Available Ecosystem Networks"
      title="Available Ecosystem Networks"
      subtitle="GBR provides configurable ecosystem network models that an ENO can use to build and operate different types of ecosystem networks."
    >
      <Grid cols={3}>
        {networks.map((n, i) => (
          <div key={i} className="tpn-card">
            <span className="tpn-badge">{n.code}</span>
            <h3 className="tpn-h3">{n.name}</h3>
            <p className="tpn-body tpn-muted">{n.desc}</p>
          </div>
        ))}
      </Grid>

      <Callout>Select a network model. Configure it. Build your ecosystem around it.</Callout>
    </Section>
  );
} */

// ─── 11 — Micro-Ecosystem Networks ────────────────────────────────────────────
function MicroEcosystemSection() {
  const examples = [
    { code: "AGN", name: "Agri Asset Governance Network" },
    { code: "PPN", name: "Plant Production Network" },
    { code: "CBN", name: "Community Based Network" },
    { code: "ARN", name: "Agro-Retailer Network" },
    { code: "LTN", name: "Local Trade Network" },
    { code: "FPN", name: "Field Production Network" },
    { code: "BPN", name: "Biochar Production Network" },
    { code: "DSN", name: "Dark Store Network" },
    { code: "SN", name: "Storage Network" },
    { code: "TN", name: "Transport Network" },
    { code: "BgPN", name: "BioGas Production Network" },
    { code: "AMN", name: "Artisan Manager Network" }
  ];
  const kinds = ["Farm Micro-Ecosystems", "Enterprise Micro-Ecosystems", "Community Micro-Ecosystems"];
  const includes = [
    "Sites and locations", "Physical and digital assets", "Local staff", "Micro-entrepreneurs / rural entrepreneurs",
    "Local field agents", "Local field operators", "Service providers", "Developers and QA teams",
    "Local workflows", "Local operations",
  ];

  return (
    <Section tint
      label="Micro-Ecosystem Networks"
      title="Extend Your Ecosystem Into Micro-Ecosystems"
      subtitle="Create focused operational networks around specific production activities, services, communities, business functions or local operations."
    >
      <p className="tpn-h3" style={{ marginBottom: "24px" }}>Micro-Ecosystem Network Examples</p>
      <Grid cols={3}>
        {examples.map((e, i) => (
          <div key={i} className="tpn-card">
            <span className="tpn-badge">{e.code}</span>
            <p className="tpn-body tpn-strong">{e.name}</p>
          </div>
        ))}
      </Grid>

      <div className="tpn-block"><Chips items={kinds} /></div>

      <div className="tpn-panel tpn-block">
        <h3 className="tpn-h3">Each Micro-Ecosystem Network can include its own:</h3>
        <CheckList items={includes} columns />
      </div>

      <Callout>One ecosystem network can operate many interconnected micro-ecosystem networks.</Callout>
    </Section>
  );
}

// ─── 12 — Participants & Digital Tools ────────────────────────────────────────
/* function ParticipantsToolsSection() {
  const basedOn = ["User Type", "Ecosystem Role", "Network Model", "Package", "Operating responsibilities"];
  const participants = [
    { icon: "🌾", name: "Farmers", desc: "Farm and production operations." },
    { icon: "🏭", name: "Manufacturers", desc: "Procurement, production and supplier coordination." },
    { icon: "🏷️", name: "Brands", desc: "Sourcing, manufacturing and supply chain operations." },
    { icon: "🛠️", name: "Service Providers", desc: "Service delivery and field operations." },
    { icon: "🧭", name: "ENO Teams", desc: "Network governance, intelligence and ecosystem operations." },
  ];
  const tools = [
    { icon: "👥", label: "Participant Management" },
    { icon: "🌐", label: "Ecosystem Network Management" },
    { icon: "📍", label: "Site & Asset Management" },
    { icon: "🔄", label: "Workflow & Operations" },
    { icon: "📊", label: "Data & Intelligence" },
    { icon: "🤖", label: "AI Agents" },
    { icon: "📡", label: "Device & IoT Management" },
    { icon: "🔐", label: "Governance & Administration" },
  ];

  return (
    <Section
      tint
      label="Participants & Digital Tools"
      title="Equip Every Participant to Operate"
      subtitle="Every ecosystem participant has different operational responsibilities."
    >
      <div className="tpn-panel tpn-block">
        <h3 className="tpn-h3" style={{ marginBottom: "20px" }}>The ENO can provide configurable digital tools based on:</h3>
        <Chips items={basedOn} />
      </div>

      <Grid cols={3}>
        {participants.map((p, i) => (
          <Card key={i} icon={p.icon} title={p.name}>
            <p className="tpn-body tpn-muted">{p.desc}</p>
          </Card>
        ))}
      </Grid>

      <p className="tpn-h3" style={{ marginBottom: "24px" }}>Digital tools can include:</p>
      <Grid cols={4}>
        {tools.map((t, i) => <LabelItem key={i} icon={t.icon}>{t.label}</LabelItem>)}
      </Grid>

      <Callout>The right participant gets the right digital tools for the job.</Callout>
    </Section>
  );
} */

// ─── 13 — Ecosystem Packages (tiered ladder) ──────────────────────────────────
/* function PackagesSection() {
  const tiers = ["Free", "Basic", "Standard", "Preferred", "Premium"];
  const provides = ["Digital tools", "Services", "Data access", "Workflows", "Intelligence", "Network capabilities"];

  return (
    <Section
      label="Ecosystem Packages"
      title="Give Each Participant the Right Experience"
      subtitle="Participants do not need the same capabilities."
    >
      <p className="tpn-body" style={{ marginBottom: "24px" }}>Your ecosystem can provide configurable packages such as:</p>
      <ol className="tpn-ladder tpn-ladder--up">
        {tiers.map((t, i) => (
          <li key={i} style={{ "--i": i }}>
            <span>{t}</span>
            <span className="tpn-ladder__tier">Level {i + 1}</span>
          </li>
        ))}
      </ol>

      <div className="tpn-panel tpn-block">
        <h3 className="tpn-h3">Each package can provide a different combination of:</h3>
        <CheckList items={provides} columns />
      </div>

      <Callout>The ENO defines what each participant can access.</Callout>
      <Note>Packages can be configured according to the participant's role, operating requirements and relationship with the ecosystem.</Note>
    </Section>
  );
} */

// ─── 14 — AI-Native Ecosystem Operations ──────────────────────────────────────
function AISection() {
  const abilities = [
    "Understand ecosystem intent", "Assist participants", "Coordinate workflows", "Monitor operations",
    "Analyse ecosystem data", "Identify operational events", "Support decision-making", "Automate ecosystem processes",
  ];

  return (
    <Section
     
      label="AI-Native Ecosystem Operations"
      title="Let AI Work Across Your Ecosystem"
      subtitle="Deploy AI agents to support ecosystem operations."
    >
      <p className="tpn-h3" style={{ marginBottom: "24px" }}>AI agents can:</p>
      <Grid cols={4}>
        {abilities.map((a, i) => <LabelItem key={i} icon="">{a}</LabelItem>)}
      </Grid>

      <Callout ink>
        AI doesn't just assist an application.<br />
        <span className="tpn-em">It can work across the ecosystem.</span>
      </Callout>
      <Note>AI becomes part of the operating environment rather than being limited to an individual application.</Note>
    </Section>
  );
}

// ─── 15 — Own the Operating Environment ───────────────────────────────────────
function OwnEnvironmentSection() {
  const controls = [
    { icon: "", title: "Participants", desc: "Who joins the ecosystem and what they can access." },
    { icon: "", title: "Digital Tools", desc: "What capabilities are provided to participants and operators." },
    { icon: "", title: "Workflows", desc: "How ecosystem activities are executed." },
    { icon: "", title: "Data", desc: "How ecosystem information is captured, accessed and used." },
    { icon: "", title: "Assets", desc: "How physical and digital assets participate in ecosystem operations." },
    { icon: "", title: "AI", desc: "How intelligence and AI agents support ecosystem activities." },
    { icon: "", title: "Governance", desc: "How the ecosystem operates, evolves and remains governed." },
    { icon: "", title: "Operations", desc: "How the ecosystem is continuously managed and scaled." },
  ];

  return (
    <Section
      tint
      label="Your Ecosystem. Your Infrastructure. Your Control."
      title="Own the Operating Environment"
      subtitle="As the ENO, your organisation operates the infrastructure through which your ecosystem functions. You maintain operational control over:"
    >
      <Grid cols={4}>
        {controls.map((c, i) => (
          <Card key={i} icon={c.icon} title={c.title}>
            <p className="tpn-body tpn-muted">{c.desc}</p>
          </Card>
        ))}
      </Grid>

      <Callout>Your ecosystem. Your infrastructure. Your operating environment.</Callout>
    </Section>
  );
}

// ─── 16 — Network in Action ───────────────────────────────────────────────────
/* function NetworkInActionSection() {
  const roles = [
    { icon: "🌾", name: "Farmers", desc: "Manage production, assets and field activities." },
    { icon: "🛠️", name: "Service Providers", desc: "Deliver agricultural and field services." },
    { icon: "🏭", name: "Manufacturers", desc: "Fulfil production and sourcing requirements." },
    { icon: "🏷️", name: "Brands", desc: "Manage sourcing, manufacturing and supply." },
    { icon: "🛒", name: "Buyers", desc: "Discover and access verified produce." },
    { icon: "🤖", name: "AI Agents", desc: "Assist with coordination, intelligence and operations." },
    { icon: "🧭", name: "ENO", desc: "Governs and orchestrates the entire ecosystem.", accent: true },
  ];

  return (
    <Section
      tint
      label="Network in Action"
      title="See Your Ecosystem at Work"
      subtitle="Imagine a distributed agribusiness ecosystem where:"
    >
      <Grid cols={4}>
        {roles.map((r, i) => (
          <Card key={i} icon={r.icon} title={r.name} accent={r.accent}>
            <p className={`tpn-body${r.accent ? "" : " tpn-muted"}`}>{r.desc}</p>
          </Card>
        ))}
      </Grid>

      <Callout ink>
        One operating environment. Many organisations.<br />
        <span className="tpn-em">One connected ecosystem.</span>
      </Callout>
    </Section>
  );
} */

// ─── 17 — Value, Monetization & Scale ─────────────────────────────────────────
function ValueSection() {
  const pillars = [
    { icon: "", title: "Operate", desc: "Coordinate distributed participants, workflows, assets and operations." },
    { icon: "", title: "Expand", desc: "Reach more participants, locations and markets." },
    { icon: "", title: "Innovate", desc: "Introduce new ecosystem capabilities and business models." },
    { icon: "", title: "Scale", desc: "Replicate and expand ecosystem networks across locations, business areas and markets." },
  ];
  const monetize = [
    "Participant packages", "Digital tools", "Ecosystem services", "Network subscriptions", "Transactions",
    "Asset services", "Professional services", "Premium capabilities", "Ecosystem-based business models",
  ];

  return (
    <Section
      label="Value, Monetization & Scale"
      title="Turn Your Ecosystem Into an Operating Business"
      subtitle="Your ecosystem can enable your organisation to:"
    >
      <Grid cols={4}>
        {pillars.map((p, i) => (
          <Card key={i} icon={p.icon} title={p.title}>
            <p className="tpn-body tpn-muted">{p.desc}</p>
          </Card>
        ))}
      </Grid>

      <div className="tpn-panel tpn-block">
        <h3 className="tpn-h3">💰 Monetize</h3>
        <p className="tpn-body tpn-muted" style={{ marginBottom: "20px" }}>Create value through:</p>
        <Chips items={monetize} />
      </div>

      <Callout>Build the network. Operate the network. Create value from the network.</Callout>
    </Section>
  );
}

// ─── 18 — Scale (progression funnel) ──────────────────────────────────────────
/* function ScaleSection() {
  const stages = ["One Network", "Multiple Networks", "Micro-Ecosystems", "Connected Ecosystem Networks", "Network of Ecosystems"];

  return (
    <Section
      tint
      label="Scale"
      title="From One Network to a Network of Ecosystems"
      subtitle="Start with one ecosystem network and expand progressively."
    >
      <ol className="tpn-funnel">
        {stages.map((s, i) => (
          <React.Fragment key={i}>
            <li style={{ "--i": i }}>{s}</li>
            {i < stages.length - 1 && <li className="tpn-arrow" aria-hidden="true">↓</li>}
          </React.Fragment>
        ))}
      </ol>
    </Section>
  );
} */

// ─── Main page component for TPN ─────────────────────────────────────────────
export default function TPN() {
  // Intercept the "See How It Works" banner button after render.
  // UpdatesBanner renders href6 as an <a> tag — we find it by its text
  // and replace the default navigation with a smooth scroll.
  useEffect(() => {
    const scrollToSection = (e) => {
      const target = document.getElementById("how-your-network-works");
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };

    // Find the anchor whose text matches the button label
    const anchors = document.querySelectorAll("a");
    let seeHowBtn = null;
    anchors.forEach((a) => {
      if (a.textContent.trim() === "See How It Works") {
        seeHowBtn = a;
      }
    });

    if (seeHowBtn) {
      seeHowBtn.addEventListener("click", scrollToSection);
    }

    return () => {
      if (seeHowBtn) {
        seeHowBtn.removeEventListener("click", scrollToSection);
      }
    };
  }, []);

  // Entrance animation: fade + slight upward slide, once per section.
  // (CSS already disables the animation when prefers-reduced-motion is on.)
  useEffect(() => {
    const items = document.querySelectorAll(".tpn-reveal");
    if (!("IntersectionObserver" in window)) {
      items.forEach((el) => el.classList.add("is-visible"));
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target); // play once, never replay
          }
        });
      },
      { threshold: 0.05 }
    );
    items.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/* ── 1. Hero / Banner ── */}
      <UpdatesBanner
        className="tpn-bg"
        bannerTitle={["Build Your Ecosystem Network"]}
        animation="true"
        bannerbodycopy1={[<><br />Build, launch and operate your own ecosystem network—bringing participants, organisations, services, digital tools, data, AI agents, devices, physical assets and workflows together through sovereign operational infrastructure you control.</>]}
        buttonText7="Start Building Your Ecosystem Networks"
        href7="/si-registration"
        buttonText6="See How It Works"
        href6="#how-your-network-works"
      />

      {/* Background rhythm: Hero white → 02 tint → 03 white → 04 tint … */}
      <WhyNowSection />              {/* 02 tint  */}
      <ProblemSection />             {/* 03 white */}
      <TransformationSection />      {/* 04 tint  */}
      <WhatCanYouBuildSection />     {/* 05 white */}
     {/*  <WhatIsNetworkSection />  */}      {/* 06 tint  */}
      <HowNetworkWorksSection />     {/* 07 white */}
      {/* <ConfigureSection /> */}           {/* 08 tint  */}
      <NetworkModelsSection />       {/* 09 white */}
     {/*  <AvailableNetworksSection /> */}   {/* 10 tint  */}
      <MicroEcosystemSection />      {/* 11 white */}
     {/*  <ParticipantsToolsSection />  */}  {/* 12 tint  */}
     {/*  <PackagesSection />      */}       {/* 13 white */}
      <AISection />                  {/* 14 tint  */}
      <OwnEnvironmentSection />      {/* 15 white */}
    {/*   <NetworkInActionSection />   */}   {/* 16 tint  */}
      <ValueSection />               {/* 17 white */}
     {/*  <ScaleSection />    */}            {/* 18 tint  */}

      {/* ── Final CTA ── */}
      <section className="tpn-cta-section">
        <div className="tpn-container">
          <CTA
            data="present"
            href="/si-registration"
            heading={
              <div className="cta-heading">
                <h2>Ready to Own &amp; Operate Your Ecosystem Network?</h2>
                <p style={{ marginTop: "10px" }}>
                  Turn your ecosystem vision into a functioning digital operating environment.
                </p>
                <p style={{ fontWeight: 700 }}>Build. Launch. Operate. Scale.</p>
              </div>
            }
            btnText="Orchestrate Your Network"
          />
        </div>
      </section>
    </>
  );
}
